import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, CircularProgress } from '@mui/material';
import EventList from '../components/GiftCardList/GiftCardList';
import axios from 'axios';
import { useLocation } from "react-router-dom";
import { trackHomePageView } from '../utils/googleAnalyticsReport';

interface Event {
    id: string;
    name: string;
    price: number;
    description: string;
    image: string;
    _id: string;
}

const API_URL = `${process.env.REACT_APP_BASE_API_URL}/v1/giftcards`;

const GiftCards: React.FC = () => {
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const location = useLocation();
    const hasTracked = useRef(false);

    const fetchGiftCards = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_URL);
            console.log("Response Data:", response.data);

            const eventData = response.data.data.map((item: any) => ({
                id: item._id,
                name: item.name,
                price: item.price,
                description: item.description,
                image: item.image,
            }));

            setEvents(eventData);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Error fetching gift cards. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGiftCards();

        if (!hasTracked.current && location.pathname === '/giftcards') {
            trackHomePageView('/giftcards');
            hasTracked.current = true;
        }
    }, [location.pathname]);

    const content = useMemo(() => {
        if (loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                </div>
            );
        }

        if (error) {
            return <p>{error}</p>;
        }

        const mappedEvents = events.map(event => ({
            imageUrl: event.image,
            title: event.name,
            description: event.description,
            price: `$${event.price}`,
            giftcardId: `${event.id}`,
        }));

        return events.length > 0 ? (
            <EventList events={mappedEvents} />
        ) : (
            <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <p style={{ margin: 0 }}>No gift cards available.</p>
            </div>
        );
    }, [loading, error, events]);

    return (
        <Container style={{ fontFamily: 'Poppins, Arial, Helvetica, Verdana, Tahoma, sans-serif', maxWidth: 'none', width: '100%' }}>
            {content}
        </Container>
    );
};

export default GiftCards;
